/**
 * Global Edge Function State Manager
 * Tracks all active edge function calls across the application
 * for coordinated loading indicators and debugging
 */

type EdgeFunctionCallInfo = {
  functionName: string;
  startTime: number;
  requestId: string;
};

type EdgeFunctionListener = (activeCalls: Map<string, EdgeFunctionCallInfo>) => void;

class EdgeFunctionManager {
  private activeCalls = new Map<string, EdgeFunctionCallInfo>();
  private listeners = new Set<EdgeFunctionListener>();
  private requestCounter = 0;

  /**
   * Register a new edge function call
   */
  startCall(functionName: string): string {
    const requestId = `${functionName}_${Date.now()}_${++this.requestCounter}`;
    
    this.activeCalls.set(requestId, {
      functionName,
      startTime: Date.now(),
      requestId,
    });
    
    this.notifyListeners();
    
    return requestId;
  }

  /**
   * Mark an edge function call as complete
   */
  endCall(requestId: string): void {
    if (this.activeCalls.has(requestId)) {
      const callInfo = this.activeCalls.get(requestId)!;
      const duration = Date.now() - callInfo.startTime;
      
      console.log(`[EdgeFunction] ${callInfo.functionName} completed in ${duration}ms`);
      
      this.activeCalls.delete(requestId);
      this.notifyListeners();
    }
  }

  /**
   * Check if any edge functions are currently executing
   */
  isAnyCallActive(): boolean {
    return this.activeCalls.size > 0;
  }

  /**
   * Get all currently active calls
   */
  getActiveCalls(): Map<string, EdgeFunctionCallInfo> {
    return new Map(this.activeCalls);
  }

  /**
   * Subscribe to edge function state changes
   */
  subscribe(listener: EdgeFunctionListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener(new Map(this.activeCalls));
    });
  }

  /**
   * Clear all active calls (useful for cleanup/testing)
   */
  clear(): void {
    this.activeCalls.clear();
    this.notifyListeners();
  }
}

// Export singleton instance
export const edgeFunctionManager = new EdgeFunctionManager();
